import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Sparkles, Users, DollarSign, BarChart3, Video, Plus, Trash2, Edit2,
  LogOut, Home, FileText, Calendar, Download, Eye, ChevronUp, ChevronDown, 
  Power, PowerOff, Settings, ExternalLink, Tag, Percent, Gift, Clock, Copy
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [togglingUserId, setTogglingUserId] = useState(null);
  
  // Video form state
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '',
    youtube_url: '',
    description: '',
    category: 'tutorial'
  });

  // User edit state
  const [editingUser, setEditingUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  
  // Settings state
  const [youtubeChannelUrl, setYoutubeChannelUrl] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Pricing state
  const [pricingForm, setPricingForm] = useState({ price: 55, access_duration_days: 90 });
  const [savingPricing, setSavingPricing] = useState(false);
  // Collaborators state
  const [projectsWithCollabs, setProjectsWithCollabs] = useState([]);
  const [collabEditingProject, setCollabEditingProject] = useState(null);
  const [collabForm, setCollabForm] = useState({ email: '', password: '', full_name: '' });
  const [savingCollab, setSavingCollab] = useState(false);
  const [allCollaborators, setAllCollaborators] = useState([]);
  const [collabMode, setCollabMode] = useState('new'); // 'new' | 'existing'
  
  // Promo state
  const [promo, setPromo] = useState({
    is_active: false,
    promo_price: 55.0,
    promo_end_date: '',
    promo_image_url: '',
    promo_video_url: '',
    promo_title: 'Special Offer',
    promo_description: ''
  });
  const [savingPromo, setSavingPromo] = useState(false);
  
  // Discount codes state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: '',
    expires_at: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes, subsRes, videosRes, settingsRes, promoRes, discountRes, pricingRes, projectsRes, allCollabsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/subscriptions`, { headers }),
        axios.get(`${API}/admin/videos`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }),
        axios.get(`${API}/admin/promo`, { headers }),
        axios.get(`${API}/admin/discount-codes`, { headers }),
        axios.get(`${API}/admin/pricing`, { headers }),
        axios.get(`${API}/admin/projects-with-collaborators`, { headers }),
        axios.get(`${API}/admin/collaborators`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSubscriptions(subsRes.data);
      setVideos(videosRes.data);
      setYoutubeChannelUrl(settingsRes.data.youtube_channel_url || '');
      setPromo(promoRes.data);
      setDiscountCodes(discountRes.data);
      setPricingForm({ price: pricingRes.data.price, access_duration_days: pricingRes.data.access_duration_days });
      setProjectsWithCollabs(projectsRes.data);
      setAllCollaborators(allCollabsRes.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveYoutubeChannel = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings/youtube-channel`, 
        { url: youtubeChannelUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('YouTube channel URL saved');
    } catch (err) {
      toast.error('Failed to save setting');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/admin/pricing`, 
        { price: parseFloat(pricingForm.price), access_duration_days: parseInt(pricingForm.access_duration_days) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pricing updated successfully. Changes apply to all new purchases.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const openCollabModal = (project) => {
    setCollabEditingProject(project);
    // Default to 'new' mode. If there's already a collaborator on this project, prefill fields.
    setCollabMode('new');
    setCollabForm({ email: project.collaborator?.email || '', password: '', full_name: project.collaborator?.full_name || '' });
  };

  const handleSaveCollaborator = async () => {
    if (collabMode === 'existing') {
      if (!collabForm.email) {
        toast.error('Please select an existing supervisor');
        return;
      }
    } else {
      if (!collabForm.email || !collabForm.password) {
        toast.error('Email and password are required for a new supervisor');
        return;
      }
    }
    setSavingCollab(true);
    try {
      await axios.post(
        `${API}/admin/projects/${collabEditingProject.project_id}/collaborator`,
        { email: collabForm.email, password: collabForm.password || '', full_name: collabForm.full_name || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(collabMode === 'existing' ? 'Supervisor attached to project' : 'Supervisor created and attached');
      setCollabEditingProject(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save collaborator');
    } finally {
      setSavingCollab(false);
    }
  };

  const handleRemoveCollaborator = async (projectId) => {
    if (!window.confirm('Detach the supervisor from this project? If they have no other projects, their account will be deleted.')) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}/collaborator`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supervisor detached');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', videoForm.title);
      formData.append('youtube_url', videoForm.youtube_url);
      formData.append('description', videoForm.description);
      formData.append('category', videoForm.category);

      if (editingVideo) {
        await axios.put(`${API}/admin/videos/${editingVideo.id}`, videoForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video updated');
      } else {
        await axios.post(`${API}/admin/videos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video added');
      }
      
      setVideoDialogOpen(false);
      setEditingVideo(null);
      setVideoForm({ title: '', youtube_url: '', description: '', category: 'tutorial' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete video');
    }
  };

  const handleMoveVideo = async (videoId, direction) => {
    const currentIndex = videos.findIndex(v => v.id === videoId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= videos.length) return;
    
    // Create new order
    const newVideos = [...videos];
    [newVideos[currentIndex], newVideos[newIndex]] = [newVideos[newIndex], newVideos[currentIndex]];
    
    // Send reorder request
    const videoOrders = newVideos.map((v, i) => ({ id: v.id, order: i + 1 }));
    
    try {
      await axios.post(`${API}/admin/videos/reorder`, videoOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(newVideos);
      toast.success('Video order updated');
    } catch (err) {
      toast.error('Failed to reorder videos');
    }
  };

  const handleToggleUserStatus = async (userId) => {
    setTogglingUserId(userId);
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      // Update the users list
      setUsers(prev => prev.map(u => 
        u.id === userId 
          ? { ...u, subscription_status: res.data.new_status }
          : u
      ));
      // Refresh stats
      fetchData();
    } catch (err) {
      toast.error('Failed to update user status');
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title,
      youtube_url: video.youtube_url,
      description: video.description || '',
      category: video.category
    });
    setVideoDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, editingUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated');
      setUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleViewInvoice = async (subscriptionId) => {
    try {
      const res = await axios.get(`${API}/admin/invoice/${subscriptionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const invoice = res.data;
      
      // Generate PDF-like invoice in new window
      const invoiceHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
            .logo { font-size: 24px; font-weight: bold; color: #4338ca; }
            .invoice-title { font-size: 28px; color: #333; }
            .invoice-details { margin-bottom: 30px; }
            .customer-info { background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
            th { background: #f1f5f9; }
            .total-row { font-weight: bold; font-size: 18px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">StatsXY</div>
            <div class="invoice-title">INVOICE</div>
          </div>
          <div class="invoice-details">
            <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
            <p><strong>Date:</strong> ${new Date(invoice.date).toLocaleDateString()}</p>
            <p><strong>Status:</strong> ${invoice.status}</p>
            <p><strong>Order ID:</strong> ${invoice.order_id}</p>
            <p><strong>Payment Method:</strong> PayPal</p>
          </div>
          <div class="customer-info">
            <h3>Bill To:</h3>
            <p><strong>${invoice.customer.name}</strong></p>
            <p>${invoice.customer.email}</p>
            <p>${invoice.customer.institution}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td>${item.quantity}</td>
                  <td>$${item.unit_price.toFixed(2)}</td>
                  <td>$${item.total.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3">Subtotal</td>
                <td>$${invoice.subtotal.toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="3">Tax</td>
                <td>$${invoice.tax.toFixed(2)}</td>
              </tr>
              <tr class="total-row">
                <td colspan="3">Total</td>
                <td>$${invoice.total.toFixed(2)} ${invoice.currency}</td>
              </tr>
            </tfoot>
          </table>
          <div class="footer">
            <p>Thank you for your subscription to StatsXY!</p>
            <p>For questions, contact support@statsxy.com</p>
          </div>
          <script>window.print();</script>
        </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(invoiceHtml);
      printWindow.document.close();
    } catch (err) {
      toast.error('Failed to generate invoice');
    }
  };
  
  const handleDownloadPdf = async (subscriptionId) => {
    try {
      const response = await axios.get(`${API}/admin/invoice/${subscriptionId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${subscriptionId.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error('Failed to download PDF invoice');
    }
  };

  // Promo handlers
  const handleSavePromo = async () => {
    setSavingPromo(true);
    try {
      await axios.post(`${API}/admin/promo`, promo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promotion settings saved');
    } catch (err) {
      toast.error('Failed to save promotion settings');
    } finally {
      setSavingPromo(false);
    }
  };

  // Discount code handlers
  const handleCreateDiscountCode = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: discountForm.code,
        discount_type: discountForm.discount_type,
        discount_value: parseFloat(discountForm.discount_value),
        max_uses: discountForm.max_uses ? parseInt(discountForm.max_uses) : null,
        expires_at: discountForm.expires_at || null
      };
      
      await axios.post(`${API}/admin/discount-codes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discount code created');
      setDiscountDialogOpen(false);
      setDiscountForm({ code: '', discount_type: 'percentage', discount_value: 10, max_uses: '', expires_at: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create discount code');
    }
  };

  const handleToggleDiscountCode = async (codeId) => {
    try {
      await axios.put(`${API}/admin/discount-codes/${codeId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discount code status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to toggle discount code');
    }
  };

  const handleDeleteDiscountCode = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discount code deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete discount code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-slate-300 p-6 hidden lg:block">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-serif font-bold text-white">StatsXY Admin</span>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <Home className="w-5 h-5" />
            User Dashboard
          </Link>
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <BarChart3 className="w-5 h-5" />
            Admin Panel
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <button 
            onClick={logout}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors w-full px-4 py-2"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-10">
          <div className="mb-8">
            <h1 className="text-3xl font-serif font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600">Manage users, subscriptions, and content</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border border-slate-200 p-1 flex-wrap">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="collaborators" data-testid="tab-collaborators">Collaborators</TabsTrigger>
              <TabsTrigger value="subscriptions">Purchases</TabsTrigger>
              <TabsTrigger value="promotions">Promotions</TabsTrigger>
              <TabsTrigger value="videos">Videos</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card-dashboard">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-slate-600">Total Users</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                </div>
                <div className="card-dashboard">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-slate-600">Active Access</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats?.active_subscriptions || 0}</p>
                </div>
                <div className="card-dashboard">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-slate-600">Projects</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_projects || 0}</p>
                </div>
                <div className="card-dashboard">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-indigo-600" />
                    </div>
                    <span className="text-slate-600">Total Revenue</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
              </div>

              {/* Revenue Metrics Section */}
              <div className="card-dashboard">
                <h3 className="text-lg font-serif font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Revenue Metrics
                </h3>
                <div className="grid sm:grid-cols-3 gap-6">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <p className="text-sm text-green-700 font-medium mb-1">Current Price</p>
                    <p className="text-2xl font-bold text-green-800" data-testid="current-price-value">${stats?.current_price?.toFixed(2) || '55.00'}</p>
                    <p className="text-xs text-green-600 mt-1">{stats?.access_duration_days ? `${Math.round(stats.access_duration_days / 30)} months access` : '3 months access'}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <p className="text-sm text-blue-700 font-medium mb-1">Purchases This Month</p>
                    <p className="text-2xl font-bold text-blue-800" data-testid="purchases-this-month">{stats?.purchases_this_month || 0}</p>
                    <p className="text-xs text-blue-600 mt-1">New purchases</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-100">
                    <p className="text-sm text-purple-700 font-medium mb-1">Revenue This Month</p>
                    <p className="text-2xl font-bold text-purple-800" data-testid="revenue-this-month">${stats?.revenue_this_month?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-purple-600 mt-1">From new purchases</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-6">
              <div className="card-dashboard">
                <h3 className="text-lg font-serif font-semibold mb-4">All Users</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left">Name</th>
                        <th className="px-4 py-3 text-left">Email</th>
                        <th className="px-4 py-3 text-left">University</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Last Sign In</th>
                        <th className="px-4 py-3 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b">
                          <td className="px-4 py-3 font-medium">{u.full_name}</td>
                          <td className="px-4 py-3">{u.email}</td>
                          <td className="px-4 py-3">{u.university}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              u.subscription_status === 'active' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {u.subscription_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {u.last_sign_in ? new Date(u.last_sign_in).toLocaleString() : 'Never'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleToggleUserStatus(u.id)}
                                disabled={togglingUserId === u.id}
                                className={`p-1.5 rounded transition-colors ${
                                  u.subscription_status === 'active'
                                    ? 'text-green-500 hover:text-red-600 hover:bg-red-50'
                                    : 'text-slate-400 hover:text-green-600 hover:bg-green-50'
                                }`}
                                title={u.subscription_status === 'active' ? 'Deactivate user' : 'Activate user'}
                                data-testid={`toggle-status-${u.id}`}
                              >
                                {togglingUserId === u.id ? (
                                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : u.subscription_status === 'active' ? (
                                  <Power className="w-4 h-4" />
                                ) : (
                                  <PowerOff className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => { setEditingUser({...u}); setUserDialogOpen(true); }}
                                className="p-1.5 text-slate-400 hover:text-indigo-600"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-1.5 text-slate-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Edit User Dialog */}
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit User</DialogTitle>
                  </DialogHeader>
                  {editingUser && (
                    <form onSubmit={handleUpdateUser} className="space-y-4">
                      <div>
                        <Label>Full Name</Label>
                        <Input
                          value={editingUser.full_name}
                          onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                          className="input-field mt-1"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          value={editingUser.email}
                          onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                          className="input-field mt-1"
                        />
                      </div>
                      <div>
                        <Label>Subscription Status</Label>
                        <Select
                          value={editingUser.subscription_status}
                          onValueChange={(v) => setEditingUser({...editingUser, subscription_status: v})}
                        >
                          <SelectTrigger className="input-field mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="btn-primary w-full">Save Changes</Button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Collaborators Tab */}
            <TabsContent value="collaborators" className="space-y-6">
              <div className="card-dashboard">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-serif font-semibold">Project Supervisors (Collaborators)</h3>
                  <div className="text-xs text-slate-500">One supervisor per project</div>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Add a supervisor (collaborator) to any user&apos;s project. They will receive email + password
                  (provided by you) and log in via the <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">/collaborator-login</code> page to
                  review the proposal and add inline comments in red.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-3 py-2">Project</th>
                        <th className="text-left px-3 py-2">Owner (Student)</th>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-left px-3 py-2">Supervisor</th>
                        <th className="text-left px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectsWithCollabs.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No projects yet.</td></tr>
                      )}
                      {projectsWithCollabs.map((p) => (
                        <tr key={p.project_id} className="border-t border-slate-100" data-testid={`collab-row-${p.project_id}`}>
                          <td className="px-3 py-3 max-w-xs">
                            <div className="font-medium text-slate-900 truncate">{p.project_title}</div>
                            <div className="text-xs text-slate-400">{p.project_id.substring(0, 8)}...</div>
                          </td>
                          <td className="px-3 py-3 text-slate-700">
                            <div>{p.owner_name || '—'}</div>
                            <div className="text-xs text-slate-400">{p.owner_email}</div>
                          </td>
                          <td className="px-3 py-3 capitalize text-slate-600">{p.research_type || '—'}</td>
                          <td className="px-3 py-3">
                            {p.collaborator ? (
                              <div>
                                <div className="text-slate-900">{p.collaborator.full_name}</div>
                                <div className="text-xs text-slate-500">{p.collaborator.email}</div>
                              </div>
                            ) : (
                              <span className="text-slate-400 italic">None</span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => openCollabModal(p)} data-testid={`set-collab-btn-${p.project_id}`}>
                                {p.collaborator ? 'Update' : 'Add Supervisor'}
                              </Button>
                              {p.collaborator && (
                                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleRemoveCollaborator(p.project_id)} data-testid={`remove-collab-btn-${p.project_id}`}>
                                  Remove
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Collab modal */}
              <Dialog open={!!collabEditingProject} onOpenChange={(open) => !open && setCollabEditingProject(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {collabEditingProject?.collaborator ? 'Update' : 'Add'} Supervisor for Project
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div className="text-sm text-slate-600 p-3 bg-slate-50 rounded">
                      <strong>{collabEditingProject?.project_title}</strong>
                      <div className="text-xs text-slate-500">Student: {collabEditingProject?.owner_email}</div>
                    </div>

                    {/* Mode toggle */}
                    <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                      <button
                        type="button"
                        onClick={() => { setCollabMode('new'); setCollabForm({ email: '', password: '', full_name: '' }); }}
                        className={`flex-1 px-3 py-2 text-sm rounded-md font-medium transition-colors ${collabMode === 'new' ? 'bg-white text-rose-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        data-testid="collab-mode-new"
                      >
                        New supervisor
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCollabMode('existing'); setCollabForm({ email: '', password: '', full_name: '' }); }}
                        disabled={allCollaborators.length === 0}
                        className={`flex-1 px-3 py-2 text-sm rounded-md font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${collabMode === 'existing' ? 'bg-white text-rose-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        data-testid="collab-mode-existing"
                      >
                        Attach existing ({allCollaborators.length})
                      </button>
                    </div>

                    {collabMode === 'existing' ? (
                      <div className="space-y-3">
                        <p className="text-xs text-slate-500">
                          Select an existing supervisor. They will be attached to this project — no new password required.
                        </p>
                        <div>
                          <Label>Existing supervisor</Label>
                          <select
                            value={collabForm.email}
                            onChange={(e) => {
                              const sel = allCollaborators.find((c) => c.email === e.target.value);
                              setCollabForm({ email: e.target.value, password: '', full_name: sel?.full_name || '' });
                            }}
                            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 bg-white text-sm"
                            data-testid="collab-form-existing-select"
                          >
                            <option value="">-- select supervisor --</option>
                            {allCollaborators.map((c) => (
                              <option key={c.id} value={c.email}>
                                {c.email} {c.full_name ? `(${c.full_name})` : ''} — supervising {c.projects.length} project{c.projects.length !== 1 ? 's' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        {collabForm.email && (
                          <div className="text-xs text-slate-500 p-2 bg-slate-50 rounded">
                            Current projects for this supervisor:
                            <ul className="mt-1 list-disc list-inside">
                              {(allCollaborators.find((c) => c.email === collabForm.email)?.projects || []).map((p) => (
                                <li key={p.project_id} className="truncate">{p.project_title}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <Label>Supervisor Full Name (optional)</Label>
                          <Input value={collabForm.full_name} onChange={(e) => setCollabForm({ ...collabForm, full_name: e.target.value })} placeholder="Dr John Smith" data-testid="collab-form-name" />
                        </div>
                        <div>
                          <Label>Supervisor Email</Label>
                          <Input type="email" value={collabForm.email} onChange={(e) => setCollabForm({ ...collabForm, email: e.target.value })} placeholder="supervisor@institution.edu" data-testid="collab-form-email" />
                        </div>
                        <div>
                          <Label>Password (you will share this with the supervisor)</Label>
                          <Input value={collabForm.password} onChange={(e) => setCollabForm({ ...collabForm, password: e.target.value })} placeholder="Minimum 8 characters" data-testid="collab-form-password" />
                          <p className="text-xs text-slate-500 mt-1">
                            The supervisor will use this email + password to log in at <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">/collaborator-login</code>.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setCollabEditingProject(null)}>Cancel</Button>
                      <Button onClick={handleSaveCollaborator} disabled={savingCollab} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="collab-form-save-btn">
                        {savingCollab ? 'Saving...' : (collabMode === 'existing' ? 'Attach Supervisor' : 'Create & Attach')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Subscriptions Tab */}
            <TabsContent value="subscriptions" className="space-y-6">
              <div className="card-dashboard">
                <h3 className="text-lg font-serif font-semibold mb-4">Payment History</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left">User</th>
                        <th className="px-4 py-3 text-left">Email</th>
                        <th className="px-4 py-3 text-left">Amount</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id} className="border-b">
                          <td className="px-4 py-3 font-medium">{sub.user_name || 'Unknown'}</td>
                          <td className="px-4 py-3">{sub.user_email || 'Unknown'}</td>
                          <td className="px-4 py-3 font-mono">${sub.amount?.toFixed(2)} {sub.currency}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              sub.status === 'COMPLETED' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {sub.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewInvoice(sub.id)}
                                className="flex items-center gap-1 text-slate-600 hover:text-indigo-600 text-xs"
                                title="View & Print Invoice"
                                data-testid={`view-invoice-${sub.id}`}
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </button>
                              <button
                                onClick={() => handleDownloadPdf(sub.id)}
                                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs"
                                title="Download PDF Invoice"
                                data-testid={`download-invoice-${sub.id}`}
                              >
                                <Download className="w-4 h-4" />
                                PDF
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Promotions Tab */}
            <TabsContent value="promotions" className="space-y-6">
              {/* Promo Settings Section */}
              <div className="card-dashboard">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-serif font-semibold flex items-center gap-2">
                    <Gift className="w-5 h-5 text-purple-600" />
                    Promotional Campaign
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${promo.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {promo.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <Switch
                      checked={promo.is_active}
                      onCheckedChange={(checked) => setPromo({...promo, is_active: checked})}
                      data-testid="promo-active-toggle"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Promo Title</Label>
                      <Input
                        value={promo.promo_title}
                        onChange={(e) => setPromo({...promo, promo_title: e.target.value})}
                        placeholder="Special Offer"
                        className="mt-1"
                        data-testid="promo-title-input"
                      />
                    </div>
                    <div>
                      <Label>Promo Description</Label>
                      <textarea
                        value={promo.promo_description}
                        onChange={(e) => setPromo({...promo, promo_description: e.target.value})}
                        placeholder="Get access to all features at a special price!"
                        className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        rows={3}
                        data-testid="promo-description-input"
                      />
                    </div>
                    <div>
                      <Label>Promotional Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={promo.promo_price}
                        onChange={(e) => setPromo({...promo, promo_price: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                        data-testid="promo-price-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Regular price: ${pricingForm.price} / {Math.round(pricingForm.access_duration_days / 30)} months (set in Settings tab)</p>
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="datetime-local"
                        value={promo.promo_end_date ? promo.promo_end_date.slice(0, 16) : ''}
                        onChange={(e) => setPromo({...promo, promo_end_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                        className="mt-1"
                        data-testid="promo-end-date-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Promo Image URL (optional)</Label>
                      <Input
                        value={promo.promo_image_url || ''}
                        onChange={(e) => setPromo({...promo, promo_image_url: e.target.value})}
                        placeholder="https://example.com/promo-image.jpg"
                        className="mt-1"
                        data-testid="promo-image-input"
                      />
                    </div>
                    <div>
                      <Label>Promo Video URL (YouTube, optional)</Label>
                      <Input
                        value={promo.promo_video_url || ''}
                        onChange={(e) => setPromo({...promo, promo_video_url: e.target.value})}
                        placeholder="https://youtube.com/watch?v=..."
                        className="mt-1"
                        data-testid="promo-video-input"
                      />
                    </div>
                    
                    {/* Preview */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-700 mb-2">Preview</p>
                      <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg text-white">
                        <p className="font-semibold">{promo.promo_title || 'Special Offer'}</p>
                        <p className="text-sm opacity-90">{promo.promo_description || 'Your promo description here'}</p>
                        <p className="text-2xl font-bold mt-2">${promo.promo_price}/mo</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t flex justify-end">
                  <Button 
                    onClick={handleSavePromo} 
                    disabled={savingPromo}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="save-promo-btn"
                  >
                    {savingPromo ? 'Saving...' : 'Save Promotion Settings'}
                  </Button>
                </div>
              </div>

              {/* Discount Codes Section */}
              <div className="card-dashboard">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-serif font-semibold flex items-center gap-2">
                    <Tag className="w-5 h-5 text-green-600" />
                    Discount Codes
                  </h3>
                  <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-green-600 hover:bg-green-700" data-testid="create-discount-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Code
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Discount Code</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateDiscountCode} className="space-y-4">
                        <div>
                          <Label>Code</Label>
                          <Input
                            value={discountForm.code}
                            onChange={(e) => setDiscountForm({...discountForm, code: e.target.value.toUpperCase()})}
                            placeholder="SAVE20"
                            className="mt-1"
                            required
                            data-testid="discount-code-form-input"
                          />
                          <p className="text-xs text-slate-500 mt-1">Will be converted to uppercase</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Discount Type</Label>
                            <Select
                              value={discountForm.discount_type}
                              onValueChange={(v) => setDiscountForm({...discountForm, discount_type: v})}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                                <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>{discountForm.discount_type === 'percentage' ? 'Percentage' : 'Amount'}</Label>
                            <Input
                              type="number"
                              min="0"
                              max={discountForm.discount_type === 'percentage' ? 100 : 28}
                              value={discountForm.discount_value}
                              onChange={(e) => setDiscountForm({...discountForm, discount_value: e.target.value})}
                              className="mt-1"
                              required
                              data-testid="discount-value-input"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Max Uses (optional)</Label>
                            <Input
                              type="number"
                              min="1"
                              value={discountForm.max_uses}
                              onChange={(e) => setDiscountForm({...discountForm, max_uses: e.target.value})}
                              placeholder="Unlimited"
                              className="mt-1"
                              data-testid="discount-max-uses-input"
                            />
                          </div>
                          <div>
                            <Label>Expiration (optional)</Label>
                            <Input
                              type="datetime-local"
                              value={discountForm.expires_at}
                              onChange={(e) => setDiscountForm({...discountForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                              className="mt-1"
                              data-testid="discount-expires-input"
                            />
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                          Create Discount Code
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-amber-800 text-sm">
                  <strong>Note:</strong> Discount codes cannot be used when a promotional campaign is active.
                </div>

                {discountCodes.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No discount codes created yet.</p>
                    <p className="text-sm">Click &quot;Create Code&quot; to add your first discount code.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-4 py-3 text-left">Code</th>
                          <th className="px-4 py-3 text-left">Discount</th>
                          <th className="px-4 py-3 text-left">Uses</th>
                          <th className="px-4 py-3 text-left">Expires</th>
                          <th className="px-4 py-3 text-left">Status</th>
                          <th className="px-4 py-3 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {discountCodes.map((code) => (
                          <tr key={code.id} className="border-b">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-indigo-600">{code.code}</span>
                                <button 
                                  onClick={() => copyToClipboard(code.code)}
                                  className="text-slate-400 hover:text-slate-600"
                                  title="Copy code"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1">
                                {code.discount_type === 'percentage' ? (
                                  <><Percent className="w-3 h-3" /> {code.discount_value}%</>
                                ) : (
                                  <><DollarSign className="w-3 h-3" /> {code.discount_value}</>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {code.current_uses || 0}{code.max_uses ? ` / ${code.max_uses}` : ' / ∞'}
                            </td>
                            <td className="px-4 py-3 text-slate-500">
                              {code.expires_at ? (
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(code.expires_at).toLocaleDateString()}
                                </span>
                              ) : 'Never'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                code.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {code.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleToggleDiscountCode(code.id)}
                                  className={`p-1.5 rounded transition-colors ${
                                    code.is_active
                                      ? 'text-green-500 hover:text-red-600 hover:bg-red-50'
                                      : 'text-slate-400 hover:text-green-600 hover:bg-green-50'
                                  }`}
                                  title={code.is_active ? 'Deactivate' : 'Activate'}
                                  data-testid={`toggle-discount-${code.id}`}
                                >
                                  {code.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => handleDeleteDiscountCode(code.id)}
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete"
                                  data-testid={`delete-discount-${code.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos" className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-serif font-semibold">YouTube Videos</h3>
                <Dialog open={videoDialogOpen} onOpenChange={(open) => {
                  setVideoDialogOpen(open);
                  if (!open) {
                    setEditingVideo(null);
                    setVideoForm({ title: '', youtube_url: '', description: '', category: 'tutorial' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Video
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingVideo ? 'Edit Video' : 'Add YouTube Video'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddVideo} className="space-y-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={videoForm.title}
                          onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                          placeholder="How to Upload Data"
                          className="input-field mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label>YouTube URL</Label>
                        <Input
                          value={videoForm.youtube_url}
                          onChange={(e) => setVideoForm({...videoForm, youtube_url: e.target.value})}
                          placeholder="https://youtube.com/watch?v=..."
                          className="input-field mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={videoForm.description}
                          onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                          placeholder="Brief description"
                          className="input-field mt-1"
                        />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select
                          value={videoForm.category}
                          onValueChange={(v) => setVideoForm({...videoForm, category: v})}
                        >
                          <SelectTrigger className="input-field mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tutorial">Tutorial</SelectItem>
                            <SelectItem value="marketing">Marketing</SelectItem>
                            <SelectItem value="howto">How-To Guide</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="btn-primary w-full">
                        {editingVideo ? 'Save Changes' : 'Add Video'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map((video) => (
                  <div key={video.id} className="card-dashboard">
                    <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden mb-4">
                      <img 
                        src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                        video.category === 'tutorial' ? 'bg-blue-100 text-blue-700' :
                        video.category === 'marketing' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {video.category}
                      </span>
                      {/* Order badge */}
                      <span className="absolute top-2 left-2 px-2 py-1 bg-slate-900/80 text-white rounded text-xs font-medium">
                        #{videos.indexOf(video) + 1}
                      </span>
                    </div>
                    <h4 className="font-medium mb-1">{video.title}</h4>
                    {video.description && (
                      <p className="text-sm text-slate-500 mb-3">{video.description}</p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {/* Reorder buttons */}
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMoveVideo(video.id, 'up')}
                          disabled={videos.indexOf(video) === 0}
                          className="px-2"
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMoveVideo(video.id, 'down')}
                          disabled={videos.indexOf(video) === videos.length - 1}
                          className="px-2"
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditVideo(video)}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteVideo(video.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
                {videos.length === 0 && (
                  <div className="col-span-full text-center py-12 text-slate-500">
                    <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No videos added yet. Click &quot;Add Video&quot; to get started.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              {/* Pricing Configuration */}
              <div className="card-dashboard">
                <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Pricing Configuration
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Set the price and access duration for new purchases. Changes will apply to the landing page, payment page, and all new PayPal transactions automatically.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <Label className="text-base font-medium mb-2 block">
                      Price (USD)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      value={pricingForm.price}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                      className="mb-2"
                      data-testid="pricing-price-input"
                    />
                    <p className="text-xs text-slate-400">The amount charged per access period</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <Label className="text-base font-medium mb-2 block">
                      Access Duration (days)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={pricingForm.access_duration_days}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, access_duration_days: e.target.value }))}
                      className="mb-2"
                      data-testid="pricing-duration-input"
                    />
                    <p className="text-xs text-slate-400">How many days of access per purchase (e.g. 90 = ~3 months)</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700">
                  Current: <strong>${parseFloat(pricingForm.price || 0).toFixed(2)}</strong> for <strong>{Math.round((pricingForm.access_duration_days || 0) / 30)} months</strong> ({pricingForm.access_duration_days} days) access
                </div>
                <Button 
                  onClick={handleSavePricing}
                  disabled={savingPricing}
                  className="mt-4 bg-green-600 hover:bg-green-700"
                  data-testid="save-pricing-btn"
                >
                  {savingPricing ? 'Saving...' : 'Save Pricing'}
                </Button>
              </div>

              <div className="card-dashboard">
                <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Platform Settings
                </h3>
                
                <div className="space-y-6">
                  {/* YouTube Channel URL */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <Label className="text-base font-medium mb-2 block">
                      Tutorials Link - YouTube Channel URL
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      This URL will be used for the Tutorials link in user dashboard sidebar. 
                      When users click Tutorials, they will be redirected to this YouTube channel.
                    </p>
                    <div className="flex gap-3">
                      <Input
                        placeholder="https://www.youtube.com/@YourChannel"
                        value={youtubeChannelUrl}
                        onChange={(e) => setYoutubeChannelUrl(e.target.value)}
                        className="flex-1"
                        data-testid="youtube-channel-input"
                      />
                      <Button 
                        onClick={handleSaveYoutubeChannel}
                        disabled={savingSettings}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="save-youtube-channel-btn"
                      >
                        {savingSettings ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                    {youtubeChannelUrl && (
                      <a 
                        href={youtubeChannelUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:text-indigo-700 mt-2 inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Preview link
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
